import { useMemo } from "react";
import { normalizeRecipeDetail } from "utils/aiNormalization.ts";
import type { AIRecipeDetailResult, SimplifiedRecipeSuggestion } from "../types/ai.ts";
import { computeRecipeDetailCacheKey, getAIRecipeDetail, setAIRecipeDetail } from "utils/storage.ts";
import { useAIFilteredStockData } from "hooks/useStockData.ts";
import { useAIJSONStream } from "./useAIJSONStream.ts";
import { buildPantryArray } from "utils/aiPantry.ts";

export type { SimplifiedRecipeSuggestion } from "../types/ai.ts";

// parsing handled by shared hook

export function useAIRecipeDetail(initial: { suggestion: SimplifiedRecipeSuggestion }) {
  const { suggestion } = initial;
  const { stockItemsMap } = useAIFilteredStockData();
  const cacheKey = useMemo(() => computeRecipeDetailCacheKey(suggestion.title, suggestion.description), [suggestion.title, suggestion.description]);
  const cachedDetail = getAIRecipeDetail(cacheKey);

  const pantry = useMemo(() => buildPantryArray(stockItemsMap), [stockItemsMap]);

  const buildPrompt = (): string => {
    const pantryJson = JSON.stringify(pantry, null, 2);
    return [
      "You will receive a prior suggestion and a Pantry JSON array.",
      "Pantry item schema: { name: string, daysUntilExpiry: number | null, amount: string, location: string }.",
      "Write a complete recipe for a novice cook. Include:",
      "- Ingredients with explicit amounts and units",
      "- Directions as numbered, step-by-step instructions",
      "- Include ingredient amounts in the steps",
      "- Explicitly call out concurrency, e.g., 'While X simmers, start Y'",
      "Ingredient policy: Use only items from the provided Pantry list. You may use common staples ONLY if they appear in the Pantry list. You may always use water as needed.",
      "Return STRICT JSON only (no code fences) in this schema:",
      "AIRecipeDetailResult { title, servings, total_time_minutes, ingredients: Array<{ name, amount, unit, notes? }>, steps: Array<{ number, instruction, concurrency_note? }> }",
      "",
      `Suggestion=${JSON.stringify(suggestion)}`,
      "pantry:",
      pantryJson,
    ].join("\n");
  };

  const normalizeOrNull = (parsed: unknown): AIRecipeDetailResult | null => {
    const maybe = parsed as Partial<AIRecipeDetailResult>;
    if (maybe && typeof maybe === 'object') {
      return normalizeRecipeDetail(maybe);
    }
    return null;
  };

  const { value: result, loading, error, requestStreaming, cancel, streaming, reasoningText } = useAIJSONStream<AIRecipeDetailResult>({
    buildPrompt,
    normalizeOrNull,
    initialValue: (cachedDetail?.result as AIRecipeDetailResult) ?? null,
    onFinal: (val, reasoning) => {
      if (val) setAIRecipeDetail(cacheKey, { result: val, savedAt: Date.now(), reasoning });
    },
  });

  return { result, loading, streaming, error, requestStreaming, cancel, reasoningText };
}

